import '@react-sigma/core/lib/style.css';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SigmaContainer } from '@react-sigma/core';
import Graph from 'graphology';
import louvain from 'graphology-communities-louvain';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import betweennessCentrality from 'graphology-metrics/centrality/betweenness';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NodeCircleProgram } from 'sigma/rendering';

import {
  CENTRALITY_MAX_NODES,
  CLUSTER_COLORS,
  LOUVAIN_MAX_NODES,
} from '../../constants/GraphRagConstants';
import { useDebounce } from '../../hooks/useDebounce';
import type { CustomFile } from '../../Types/file';
import { GraphData, GraphEdge, GraphNode } from '../../Types/graph';
import { safeTypeColor } from './canvas-utils';
import { CategoriesPanel } from './Components/CategoriesPanel';
import { ClustersPanel } from './Components/ClustersPanel';
import { GraphSearch } from './Components/GraphSearch';
import { CameraController } from './Controllers/CameraController';
import { GraphDataController } from './Controllers/GraphDataController';
import { GraphEventsController } from './Controllers/GraphEventsController';
import { GraphSettingsController } from './Controllers/GraphSettingsController';

export default function ProjectGraphVisualization({
  graphIndexId,
  graphData,
  isLoading,
  error,
  refetch,
  documentId,
  onClearDocumentFilter,
  height = 'calc(100vh - 260px)',
  filesForDocumentFilter,
  onDocumentFilterChange,
}: {
  graphIndexId: string;
  graphData: GraphData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  documentId?: string;
  onClearDocumentFilter?: () => void;
  height?: string | number;
  filesForDocumentFilter?: CustomFile[];
  onDocumentFilterChange?: (docId?: string) => void;
}) {
  const [showLabels, setShowLabels] = useState(true);
  const [showEdgeLabels, setShowEdgeLabels] = useState(false);

  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);

  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<GraphEdge | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const [searchMatches, setSearchMatches] = useState<Set<string>>(new Set());
  const [cameraTarget, setCameraTarget] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [clusterFilter, setClusterFilter] = useState<Set<number>>(new Set());
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [visibleCounts, setVisibleCounts] = useState<{ nodes: number; edges: number } | null>(null);

  const nodeMap = useRef(new Map<string, GraphNode>());
  const edgeMap = useRef(new Map<string, GraphEdge>());

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>(documentId);
  const [documentQuery, setDocumentQuery] = useState('');
  const debouncedDocumentQuery = useDebounce(documentQuery, 150);

  useEffect(() => {
    setSelectedDocumentId(documentId);
  }, [documentId]);

  const graph = useMemo(() => {
    const g = new Graph();

    nodeMap.current.clear();
    edgeMap.current.clear();

    if (!graphData?.nodes?.length) return g;

    graphData.nodes.forEach((node) => {
      const id = String(node.id);
      g.addNode(id, {
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 5,
        label: node.name,
        color: safeTypeColor(String(node.type)),
        entityType: String(node.type),
        documents: node.documents ?? [],
      });
      nodeMap.current.set(id, { ...node, id });
    });

    graphData.edges?.forEach((edge) => {
      const id = String(edge.id);
      const source = String(edge.sourceId);
      const target = String(edge.targetId);

      if (!g.hasNode(source) || !g.hasNode(target)) return;

      try {
        g.addEdgeWithKey(id, source, target, {
          id,
          label: String(edge.type || '').replaceAll('_', ' '),
          size: 1,
          color: '#777',
          relType: edge.type,
          documents: edge.documents ?? [],
        });
        edgeMap.current.set(id, { ...edge, id, sourceId: source, targetId: target });
      } catch {
        // ignore duplicates
      }
    });

    // Node sizing (betweenness centrality)
    if (g.order <= CENTRALITY_MAX_NODES) {
      try {
        const centralities = betweennessCentrality(g);
        const values = Object.values(centralities);
        const maxCentrality = Math.max(...values, 0.001);

        g.forEachNode((nodeId) => {
          const c = centralities[nodeId] || 0;
          const norm = c / maxCentrality;

          const MIN_SIZE = 8;
          const MAX_SIZE = 30;
          const RANGE = MAX_SIZE - MIN_SIZE;
          const GAMMA = 0.65;

          const size = MIN_SIZE + Math.pow(norm, GAMMA) * RANGE;
          const clamped = Math.max(MIN_SIZE, Math.min(MAX_SIZE, size));
          g.setNodeAttribute(nodeId, 'size', clamped);
        });
      } catch {
        // fallback size=5
      }
    }

    // Clusters (Louvain)
    try {
      const communities = g.order <= LOUVAIN_MAX_NODES ? louvain(g) : null;
      if (!communities) throw new Error('skip-louvain');

      const communityNodes = new Map<number, { nodeId: string; size: number; label: string }[]>();
      g.forEachNode((nodeId, attrs) => {
        const community = communities[nodeId];
        const list = communityNodes.get(community);
        const item = {
          nodeId,
          size: (attrs.size as number) || 1,
          label: (attrs.label as string) || nodeId,
        };

        if (list) {
          list.push(item);
        } else {
          communityNodes.set(community, [item]);
        }
      });

      const clusterLabels = new Map<number, string>();
      communityNodes.forEach((nodes, community) => {
        nodes.sort((a, b) => b.size - a.size);
        const top = nodes[0];
        const label = top.label.length > 20 ? `${top.label.substring(0, 18)}…` : top.label;
        clusterLabels.set(community, label);
      });

      g.forEachNode((nodeId) => {
        const community = communities[nodeId];
        const clusterColor = CLUSTER_COLORS[community % CLUSTER_COLORS.length];
        const clusterLabel = clusterLabels.get(community) || `Cluster ${community}`;

        g.setNodeAttribute(nodeId, 'cluster', community);
        g.setNodeAttribute(nodeId, 'clusterColor', clusterColor);
        g.setNodeAttribute(nodeId, 'clusterLabel', clusterLabel);
      });
    } catch {
      // optional
    }

    // Layout (ForceAtlas2) — adapt iterations for big graphs
    const iterations = g.order > 12000 ? 18 : g.order > 6000 ? 30 : g.order > 2500 ? 55 : 100;
    forceAtlas2.assign(g, {
      iterations,
      settings: {
        gravity: 1,
        scalingRatio: 10,
        barnesHutOptimize: g.order > 500,
        strongGravityMode: true,
        slowDown: 5,
      },
    });

    return g;
  }, [graphData]);

  // init typeFilter when graph loads
  useEffect(() => {
    if (!graph || graph.order === 0) return;
    setTypeFilter(new Set());
  }, [graph]);

  const handleNodeClick = useCallback((nodeId: string) => {
    const node = nodeMap.current.get(nodeId) || null;
    setSelectedNode(node);
    setSelectedEdge(null);
    setSelectedNodeId(nodeId);
    setHoveredNode(null);
  }, []);

  const handleEdgeClick = useCallback((edgeId: string) => {
    const edge = edgeMap.current.get(edgeId) || null;
    setSelectedEdge(edge);
    setSelectedNode(null);
    setSelectedNodeId(null);
  }, []);

  const handleStageClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setSelectedNodeId(null);
  }, []);

  const handleSearchChange = useCallback((query: string, matches: Set<string>) => {
    setSearchMatches(matches);
    if (query) {
      setSelectedNode(null);
      setSelectedEdge(null);
      setSelectedNodeId(null);
    }
  }, []);

  const handleNodeSelect = useCallback((nodeId: string) => {
    const node = nodeMap.current.get(nodeId) || null;
    setSelectedNode(node);
    setSelectedEdge(null);
    setSelectedNodeId(nodeId);
    setSearchMatches(new Set());
    setCameraTarget(nodeId);
  }, []);

  const handleTypeToggle = useCallback((type: string) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const handleSetAllTypes = useCallback((types: string[]) => {
    setTypeFilter(new Set(types));
  }, []);

  const handleClusterToggle = useCallback((cluster: number) => {
    setClusterFilter((prev) => {
      const next = new Set(prev);
      if (next.has(cluster)) next.delete(cluster);
      else next.add(cluster);
      return next;
    });
  }, []);

  const handleSetAllClusters = useCallback((clusters: number[]) => {
    setClusterFilter(new Set(clusters));
  }, []);

  const documentOptions = useMemo(() => {
    const files = (filesForDocumentFilter ?? []).filter((f) => Boolean(f.graphDocId));

    const opts = files.map((f) => {
      const label = f.relativePath ? `${f.relativePath}/${f.fileName}` : f.fileName;
      return { id: String(f.graphDocId), label, file: f };
    });

    const seen = new Set<string>();
    const unique = opts.filter((o) => {
      if (seen.has(o.id)) return false;
      seen.add(o.id);
      return true;
    });

    unique.sort((a, b) => a.label.localeCompare(b.label));
    return unique;
  }, [filesForDocumentFilter]);

  const visibleDocumentOptions = useMemo(() => {
    const q = debouncedDocumentQuery.trim().toLowerCase();

    if (q.length < 2) return [];

    const results: typeof documentOptions = [];
    for (const opt of documentOptions) {
      if (opt.label.toLowerCase().includes(q)) {
        results.push(opt);
        if (results.length >= 15) break;
      }
    }
    return results;
  }, [debouncedDocumentQuery, documentOptions]);

  const selectedDocumentOption = useMemo(() => {
    if (!selectedDocumentId) return null;
    return documentOptions.find((o) => o.id === String(selectedDocumentId)) ?? null;
  }, [documentOptions, selectedDocumentId]);

  const getNodeLabel = useCallback(
    (nodeId: string | number) => {
      if (!graph || !graph.hasNode(nodeId)) return nodeId;
      return String(graph.getNodeAttribute(nodeId, 'label') || nodeId);
    },
    [graph]
  );

  return (
    <Box sx={{ display: 'flex', height, gap: 2 }}>
      {!graphIndexId ? (
        <Paper sx={{ p: 2, width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            No graphId available for this project.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, position: 'relative' }}>
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading graph…
                  </Typography>
                </Box>
              ) : error ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="error">
                    Failed to load graph.
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {String(error)}
                  </Typography>
                </Box>
              ) : (
                <SigmaContainer
                  graph={graph}
                  style={{ height: '100%', width: '100%' }}
                  settings={{
                    nodeProgramClasses: { circle: NodeCircleProgram },
                    enableEdgeEvents: true,
                  }}
                >
                  <GraphEventsController
                    setHoveredNode={setHoveredNode}
                    onNodeClick={handleNodeClick}
                    onEdgeClick={handleEdgeClick}
                    onStageClick={handleStageClick}
                  />
                  <GraphSettingsController
                    hoveredNode={hoveredNode}
                    showLabels={showLabels}
                    showEdgeLabels={showEdgeLabels}
                  />
                  <GraphDataController
                    typeFilter={typeFilter}
                    clusterFilter={clusterFilter}
                    searchMatches={searchMatches}
                    documentId={selectedDocumentId}
                    onVisibleCountsChange={setVisibleCounts}
                  />
                  <CameraController targetNodeId={cameraTarget} />
                </SigmaContainer>
              )}
            </Box>

            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.100' }}>
              <Stack direction="row" spacing={2}>
                <Chip
                  label={`Nodes: ${visibleCounts?.nodes ?? graphData?.nodes?.length ?? 0}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Edges: ${visibleCounts?.edges ?? graphData?.edges?.length ?? 0}`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              width: isRightPanelCollapsed ? 56 : 360,
              transition: 'width 200ms ease',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
            }}
          >
            <Box
              sx={{
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isRightPanelCollapsed ? 'center' : 'space-between',
                gap: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              {!isRightPanelCollapsed && <Typography variant="h6">Graph Settings</Typography>}

              <Tooltip title={isRightPanelCollapsed ? 'Expand panel' : 'Collapse panel'}>
                <IconButton size="small" onClick={() => setIsRightPanelCollapsed((v) => !v)}>
                  {isRightPanelCollapsed ? <ChevronLeft /> : <ChevronRight />}
                </IconButton>
              </Tooltip>
            </Box>
            {!isRightPanelCollapsed && (
              <Box sx={{ p: 2, overflow: 'auto' }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Details
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Hover highlights neighbors. Click to inspect.
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} gutterBottom>
                    Filters
                  </Typography>
                  <Stack spacing={1.25}>
                    <Autocomplete
                      size="small"
                      options={visibleDocumentOptions}
                      value={selectedDocumentOption}
                      inputValue={documentQuery}
                      onInputChange={(_e, v) => setDocumentQuery(v)}
                      onChange={(_e, opt) => {
                        const next = opt?.id;

                        setSelectedDocumentId(next);
                        onDocumentFilterChange?.(next);

                        if (!next) onClearDocumentFilter?.();
                      }}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      getOptionLabel={(o) => o.label}
                      noOptionsText={
                        documentQuery.trim().length < 2
                          ? 'Type at least 2 characters to search'
                          : 'No matches'
                      }
                      filterOptions={(x) => x}
                      sx={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Document"
                          placeholder="Search documents…"
                          fullWidth
                          sx={{
                            '& .MuiAutocomplete-inputRoot': {
                              height: 40,
                              pr: '0 !important',
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '8px 0 !important',
                            },
                            '& .MuiAutocomplete-endAdornment': {
                              right: 8,
                            },
                          }}
                        />
                      )}
                    />
                    <GraphSearch
                      graph={graph}
                      typeFilter={typeFilter}
                      onSearchChange={handleSearchChange}
                      onNodeSelect={handleNodeSelect}
                      isLoading={isLoading}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showLabels}
                          onChange={(e) => setShowLabels(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Labels"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showEdgeLabels}
                          onChange={(e) => setShowEdgeLabels(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Relationships"
                    />
                    {error ? (
                      <Chip size="small" color="error" label="Retry" onClick={refetch} />
                    ) : null}
                  </Stack>
                </Paper>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Categories
                </Typography>
                <CategoriesPanel
                  graph={graph}
                  typeFilter={typeFilter}
                  onToggle={handleTypeToggle}
                  onSetAll={handleSetAllTypes}
                  documentId={selectedDocumentId}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Clusters
                </Typography>
                <ClustersPanel
                  graph={graph}
                  clusterFilter={clusterFilter}
                  onToggle={handleClusterToggle}
                  onSetAll={handleSetAllClusters}
                  documentId={selectedDocumentId}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  {selectedEdge ? 'Selected Edge' : 'Selected Node'}
                </Typography>

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Click a node or relationship in the graph to inspect.
                </Typography>

                {selectedNode ? (
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedNode.name}
                    </Typography>
                    <Chip
                      label={String(selectedNode.type)}
                      size="small"
                      sx={{
                        mt: 0.5,
                        bgcolor: safeTypeColor(String(selectedNode.type)),
                        color: 'white',
                      }}
                    />
                    {selectedNodeId && graph.hasNode(selectedNodeId) ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.75 }}
                      >
                        Connections: {graph.degree(selectedNodeId)}
                      </Typography>
                    ) : null}
                  </Box>
                ) : selectedEdge ? (
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedEdge.type.replaceAll('_', ' ')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {getNodeLabel(selectedEdge.sourceId)} → {getNodeLabel(selectedEdge.targetId)}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No selection yet.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </>
      )}

      <style>{`
        .sigma-mouse.mouse-pointer { cursor: pointer !important; }
      `}</style>
    </Box>
  );
}
